import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers/display-toast';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateOnboardingTeamMemberValidationReport } from '@/modules/onboarding/common/utils/onboarding-query-invalidation';
import { UpsertEmployeesBulkRequest } from '@/modules/onboarding/team-setup/types/request/upsert-employee-bulk.request';

import { upsertEmployeesBulk } from '../api/employees';

export function useUpsertEmployees({
  onSuccess,
}: { onSuccess?: (addedCount: number) => void } = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: (data: UpsertEmployeesBulkRequest) => upsertEmployeesBulk(data),
    onSuccess: async (_, variables) => {
      const members = variables.items;

      if (members.length === 1) {
        displayToastSuccess(
          t('authenticated.onboarding.team:addModal.userAdded', { name: members[0].name })
        );
      } else {
        displayToastSuccess(t('authenticated.onboarding.team:addModal.membersAdded'));
      }

      await invalidateOnboardingTeamMemberValidationReport(queryClient);
      onSuccess?.(members.length);
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(error, 'authenticated.team:api.error.createFailed');
    },
  });
}
